import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { lookbookApi } from "@/api/lookbook-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getErrorMessage } from "@/lib/api-error";
import {
  isLookbookPasswordRequired,
  setLookbookToken,
} from "@/lib/lookbook-access";
import { RaiseBoardNav, RaiseBoardShell, RBCard } from "./rb-ui";

/**
 * Stands in front of every /lb/ page. When the lookbook has a password and this
 * browser hasn't unlocked it, the visitor gets a password prompt instead of the
 * page; otherwise the page renders untouched.
 *
 * It runs the same ["lookbook", id] query the pages themselves read, so a
 * lookbook with no password costs no extra request — the pages pick the
 * payload up from the cache. Every other failure is left to the page, which
 * already knows how to say "this lookbook isn't available".
 */
export function LookbookPasswordGate({ children }: { children: ReactNode }) {
  const { lookbookPublicId } = useParams<{ lookbookPublicId: string }>();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [authReason, setAuthReason] = useState<string | null>(null);

  const { error, isPending } = useQuery({
    queryKey: ["lookbook", lookbookPublicId],
    queryFn: () => lookbookApi.getByPublicId(lookbookPublicId as string),
    enabled: !!lookbookPublicId,
    retry: false,
  });

  const locked = !!lookbookPublicId && isLookbookPasswordRequired(error);

  // A stored token the server no longer accepts — the owner changed the
  // password — is dead weight; drop it so the next request doesn't send it.
  useEffect(() => {
    if (locked && lookbookPublicId) setLookbookToken(lookbookPublicId, null);
  }, [locked, lookbookPublicId]);

  // Until the first answer is in, we don't know whether the lookbook is locked,
  // so the page is held back — rendering it would flash a protected lookbook's
  // name and layout before the password prompt replaces it.
  if (lookbookPublicId && isPending) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />
        <div className="flex justify-center px-4 py-32">
          <Loader2
            aria-label="Loading lookbook"
            className="h-6 w-6 animate-spin text-muted-foreground/60"
          />
        </div>
        <BoardAuthDialog
          open={!!authReason}
          onOpenChange={(open) => {
            if (!open) setAuthReason(null);
          }}
          reason={authReason ?? undefined}
        />
      </RaiseBoardShell>
    );
  }

  if (!locked) return <>{children}</>;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || !lookbookPublicId) return;
    setSubmitting(true);
    setProblem(null);
    try {
      const token = await lookbookApi.unlock(lookbookPublicId, password);
      setLookbookToken(lookbookPublicId, token);
      // Everything cached under this lookbook was fetched without the token —
      // the board, comments, votes — so all of it is refetched, not just the
      // payload this gate watches.
      await queryClient.resetQueries({ queryKey: ["lookbook", lookbookPublicId] });
      queryClient.invalidateQueries();
    } catch (err) {
      setProblem(getErrorMessage(err, "Couldn't check that password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RaiseBoardShell>
      <RaiseBoardNav onRequestAuth={setAuthReason} />
      <div className="flex justify-center px-4 py-24">
        <RBCard className="w-full max-w-sm">
          <form onSubmit={submit} className="flex flex-col items-center text-center">
            <KeyRound className="h-9 w-9 text-muted-foreground/50" />
            <h1 className="mt-4 font-serif text-2xl">
              This lookbook is password protected
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter the password you were given to view it.
            </p>
            <Input
              type="password"
              autoFocus
              autoComplete="current-password"
              aria-label="Lookbook password"
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setProblem(null);
              }}
              className="mt-6"
            />
            {problem && (
              <p role="alert" className="mt-2 self-start text-sm text-destructive">
                {problem}
              </p>
            )}
            <Button
              type="submit"
              className="mt-4 w-full rounded-full"
              disabled={!password || submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              View lookbook
            </Button>
          </form>
        </RBCard>
      </div>
      <BoardAuthDialog
        open={!!authReason}
        onOpenChange={(open) => {
          if (!open) setAuthReason(null);
        }}
        reason={authReason ?? undefined}
      />
    </RaiseBoardShell>
  );
}
